import { EnterpriseBadge_UserState as EnterpriseBadgeUserState } from '__generated__/graphql-types';

import type { CmlContent } from 'bundles/cml';
import type { Track } from 'bundles/naptimejs/resources/programCurriculums.v1';
import type { VerifiedSkillsBadgeFragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsBadgeTemplateQuery';
import type { VerifiedSkillsEnterpriseBadgeCollectionFragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseBadgeCollectionsQuery';
import type { VerifiedSkillsEnterpriseProgramsV1Fragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseProgramForCourseQuery';
import type { SkillPathwayCurriculumProductsFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillPathwayCurriculumProductsQuery';

const getMockCmlContent = (value: string, dtdId?: string): CmlContent => ({
  typeName: 'cml',
  definition: {
    dtdId: dtdId || 'id',
    value,
  },
});

/**
 * Course IDs from the existing prod skill-attainments QA data.
 * These IDs are also used as productIds in the enrolled badge collection mock.
 */
export const VERIFIED_SKILLS_MOCK_COURSE_ID_LIST = [
  'CzDuxG46EfCdeA7VR_Iplw',
  '5ZAh-bwCEfCXDBKsCHYbwQ',
  'JAncyjDnEfCrAgr_wGkBvQ',
  'xwI6zxKhEe26hQ4jkCdYhQ',
  '90bMGy3SEfGT9Qr_3Ae42Q',
] as const;

export const VERIFIED_SKILLS_MOCK_COURSE_IDS = new Set(VERIFIED_SKILLS_MOCK_COURSE_ID_LIST);
export const VERIFIED_SKILLS_MOCK_PROGRAM_ID = 'mock-program-id';
export const VERIFIED_SKILLS_MOCK_SKILL_PATH_ID = 'mock-skill-path-id';

export const VERIFIED_SKILLS_MOCK_BADGE_TEMPLATE_ID = 'mock-badge-template-id';
export const VERIFIED_SKILLS_MOCK_PATHWAY_NAME = 'Mock Badge Title for QA';

/**
 * Mock enrollment collection used by SkillPathwayEnrollmentDataProvider.
 * User state intentionally stays ENROLLED so this mock badge is never treated as issued.
 */
export const VERIFIED_SKILLS_MOCK_ENROLLED_BADGE_COLLECTION: VerifiedSkillsEnterpriseBadgeCollectionFragment = {
  __typename: 'EnterpriseBadge_badgeCollectionWithUserState',
  collectionId: 'mock-collection-id',
  badgeTemplateId: VERIFIED_SKILLS_MOCK_BADGE_TEMPLATE_ID,
  description:
    'Develop core analytics skills to deliver results, grow faster, and make a meaningful impact early in your career.',
  learningObjectives: [
    'Gain hands-on experience with the core skills and tools used by junior data analysts',
    'Learn how to clean, transform, and explore data to uncover meaningful insights',
    'Create clear, professional visuals and reports and use AI tools to boost your productivity',
  ],
  imageUrl: 'https://coursera-badge-assets.s3.amazonaws.com/preview/e505caac68fa4260b48c4c0b8590eb23.png',
  userState: EnterpriseBadgeUserState.Enrolled,
  productIds: VERIFIED_SKILLS_MOCK_COURSE_ID_LIST.map((courseId) => ({
    __typename: 'EnterpriseBadge_productId',
    id: courseId,
    type: 'VERIFIED_CERTIFICATE',
  })),
  slug: 'mock-badge-slug-for-qa',
  title: VERIFIED_SKILLS_MOCK_PATHWAY_NAME,
};

export const VERIFIED_SKILLS_MOCK_PROGRAM: VerifiedSkillsEnterpriseProgramsV1Fragment = {
  __typename: 'EnterpriseProgramsV1',
  id: VERIFIED_SKILLS_MOCK_PROGRAM_ID,
  thirdPartyOrganizationId: 'mock-third-party-org-id',
  metadata: {
    __typename: 'EnterpriseProgramsV1_org_coursera_program_ProgramMetadata',
    curriculumId: 'mock-curriculum-id',
    slug: 'mock-program-slug',
  },
  thirdPartyOrg: {
    __typename: 'ThirdPartyOrganizationsV1',
    id: 'mock-third-party-org-id',
    name: 'Mock Third Party Org',
    slug: 'mock-third-party-org',
  },
};

export const VERIFIED_SKILLS_MOCK_SKILL_PATH: Track = {
  id: VERIFIED_SKILLS_MOCK_SKILL_PATH_ID,
  title: VERIFIED_SKILLS_MOCK_PATHWAY_NAME,
  description: getMockCmlContent('mock-skill-path-description'),
  allCurriculumItemIds: VERIFIED_SKILLS_MOCK_COURSE_ID_LIST.map((courseId) => `courseId~${courseId}`),
  badgeTemplateId: VERIFIED_SKILLS_MOCK_BADGE_TEMPLATE_ID,
  creationSource: 'LEARNING_PATH',
  curriculumItems: VERIFIED_SKILLS_MOCK_COURSE_ID_LIST.map((courseId) => ({
    typeName: 'courseId',
    definition: { id: courseId },
  })),
};

export const VERIFIED_SKILLS_MOCK_SKILL_PATH_CURRICULUM_PRODUCTS: SkillPathwayCurriculumProductsFragment[] =
  VERIFIED_SKILLS_MOCK_COURSE_ID_LIST.map((courseId, index) => ({
    __typename: 'ProgramCurriculumProductsV1',
    id: `${VERIFIED_SKILLS_MOCK_PROGRAM_ID}~VerifiedCertificate~${courseId}`,
    productState: {
      __typename: 'ProgramCurriculumProductsV1_programCourseWithStateMember',
      productState: {
        __typename: 'ProgramCurriculumProductsV1_org_coursera_program_enrollment_ProgramCourseWithState',
        courseId,
        isWishlisted: false,
        enrolledAt: 1700000000000 + index,
        completedAt: null,
        lastUpdatedAt: 1700000000000 + index,
        state: 'ENROLLED',
        actions: ['SELECT', 'VIEW'],
      },
    },
  }));

/**
 * Mock issued-badge-shaped payload for unit testing only.
 */
export const VERIFIED_SKILLS_MOCK_BADGE_TEMPLATE: VerifiedSkillsBadgeFragment = {
  __typename: 'Badge_IssuedBadge',
  issuedBadgeId: 'mock-issued-badge-id',
  issuedBadgeSource: 'COURSERA',
  badgeTemplate: {
    __typename: 'Badge_BadgeTemplate',
    badgeSource: 'COURSERA',
    badgeTemplateId: VERIFIED_SKILLS_MOCK_BADGE_TEMPLATE_ID,
    imageUrl: 'https://coursera-badge-assets.s3.amazonaws.com/preview/e505caac68fa4260b48c4c0b8590eb23.png',
    title: VERIFIED_SKILLS_MOCK_PATHWAY_NAME,
  },
};
